import React, { useEffect, useMemo, useState } from 'react';

import { observer } from 'mobx-react';

import { Message } from 'semantic-ui-react';

import { kebabCase } from 'lodash';

import '../../css/AddUserInstitutionModal.less';

import { SatCoreComponent, SatCoreRegister } from '../../SatCoreRegistry';

import UserService from '../../services/UserService';

/**
 * Dialog to add (assign) an institution to a user.
 */
const AddUserInstitutionModal = observer((props = {}) => {
  const {
    buttonKeyCancelLabel = 'cancel',
    buttonKeyConfirmLabel = 'add',
    className = 'AddUserInstitutionModal',
    close = () => {},
    closeOnDimmerClick = false,
    closeOnEscape = true,
    disableKeystroke = false,
    disableMove = false,
    disableResize = true,
    districtId = null,
    existingUserInstitutions = [],
    initHeight,
    initWidth,
    minHeight = 275,
    minWidth = 600,
    onInstitutionAdded,
    onOpen = () => {},
    onRequestClose,
    open: isOpen,
    overlay = 'medium',
    searchType = 'school',
    t,
    top,
    userId
  } = props;

  const translationKeySuffix = searchType === 'institution' ? 'Institution' : 'School';

  // memoize (via useMemo) so SatCoreComponent's per-call wrapper class is
  // created once; otherwise each modal re-render returns a new class and
  // React unmounts and remounts the child (losing its useState)
  const GenericModal = useMemo(() => SatCoreComponent('GenericModal'), []);
  const UserAssignableInstitutionDropdown = useMemo(() => SatCoreComponent('UserAssignableInstitutionDropdown'), []);

  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Escape would otherwise bubble to a Semantic Modal ancestor (e.g. the
  // FullScreenModalPage wrapping Profile.js) and close that too. Semantic
  // UI's EventStack listens on document in capture phase, so we intercept
  // one level higher on window in capture phase -- ours fires first and
  // stopImmediatePropagation() prevents the document-level handlers.
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        event.stopImmediatePropagation();
        close();
      }
    };
    window.addEventListener('keydown', handleEscape, true);
    return () => window.removeEventListener('keydown', handleEscape, true);
  }, [close]);

  const handleSelect = (institution) => {
    setSelectedInstitution(institution);
    setErrorMsg('');
  };

  const handleClose = async (_event, actionButtonData = {}) => {
    const confirmed = !!(actionButtonData?.className?.includes?.(kebabCase(buttonKeyConfirmLabel)));
    if (!confirmed || !selectedInstitution) {
      close();
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await UserService.createUserInstitution({
        institutionId: selectedInstitution.id,
        userId
      });
      if (response?.status === 'SUCCESS') {
        await onInstitutionAdded?.();
        close();
      } else {
        setErrorMsg(t(`addFailedMsg${translationKeySuffix}`));
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error(err);
      setErrorMsg(t(`addFailedMsg${translationKeySuffix}`));
      setIsSubmitting(false);
    }
  };

  const renderBody = () => {
    const mainDirections = t(`mainDirections${translationKeySuffix}`);
    const secondaryDirections = t(`secondaryDirections${translationKeySuffix}`);
    return (
      <div className='add-user-institution-modal-body'>
        <div className='add-user-institution-label'>{t(`selectLabel${translationKeySuffix}`)}</div>
        <div className='add-user-institution-directions-wrapper'>
          {!!mainDirections && (
            <div className='add-user-institution-directions-main'>{mainDirections}</div>
          )}
          <div className='add-user-institution-directions-secondary'>{secondaryDirections}</div>
        </div>
        <div onKeyDown={(event) => event.stopPropagation()}>
          <UserAssignableInstitutionDropdown
            districtId={districtId}
            existingUserInstitutions={existingUserInstitutions}
            onChange={handleSelect}
            searchType={searchType} />
        </div>
        {!!errorMsg && (
          <Message className='add-user-institution-error' content={errorMsg} error />
        )}
      </div>
    );
  };

  return (
    <GenericModal
      bodyMsg={renderBody}
      buttonKeyCancelLabel={buttonKeyCancelLabel}
      buttonKeyConfirmLabel={buttonKeyConfirmLabel}
      className={className}
      classNameForGenericModalParent='generic-modal-parent-AddUserInstitutionModal'
      closeOnDimmerClick={closeOnDimmerClick}
      closeOnEscape={closeOnEscape}
      disableConfirmButton={!selectedInstitution || isSubmitting}
      disableKeystroke={disableKeystroke}
      disableMove={disableMove}
      disableResize={disableResize}
      headerMsg={t(`title${translationKeySuffix}`)}
      initHeight={initHeight || minHeight}
      initWidth={initWidth || minWidth}
      isConfirmButtonLoading={isSubmitting}
      isOpen={isOpen}
      onClose={handleClose}
      onOpen={onOpen}
      onRequestClose={onRequestClose}
      overlay={overlay}
      top={top} />
  );
});

export default AddUserInstitutionModal;

SatCoreRegister('AddUserInstitutionModal', AddUserInstitutionModal);
